import { useVuelidate } from '@vuelidate/core';
import { email, helpers, maxLength, minLength, required } from '@vuelidate/validators';
import { useToast } from 'primevue/usetoast';
import { storeToRefs } from 'pinia';

import { useFormErrors } from '@/composables/useFormErrors';
import { useRequestDemo } from '@/composables/mutations/useRequestDemo';
import { useLandingCountry } from '@/composables/landing/useLandingCountry';
import { useLandingStore } from '@/store/landingStore';
import { toUserMessage } from '@/utils/error';
import { getCountryProfile } from '@/utils/landing/countryProfiles';
import { DemoCountrySource } from '@/types/composition-functions';
import { buildDemoRequestInput, emptyDemoRequestForm, type DemoRequestForm } from '@/utils/landing/demoRequest';

const PHONE_PATTERN = /^[0-9+()\-\s]{6,30}$/;

/**
 * State and submit flow for the landing page's demo-request dialog. Client
 * rules mirror the backend's @rules so most mistakes are caught before the
 * request; anything the backend still rejects shows under its field.
 */
export function useDemoRequestForm() {
  const toast = useToast();
  const store = useLandingStore();
  const { demoKind } = storeToRefs(store);
  const { countryCode, countrySource } = useLandingCountry();
  const { requestDemo, isSubmitting } = useRequestDemo();
  const { serverErrors, handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

  const form = ref<DemoRequestForm>(emptyDemoRequestForm(countryCode.value));
  const submitted = ref(false);
  const successMessage = ref('');
  /** True once the visitor changes the country select themselves. */
  const countryTouched = ref(false);

  // The IP lookup can land after the dialog opened; follow it until the
  // visitor picks a country themselves.
  watch(countryCode, (code) => {
    if (!countryTouched.value) form.value.country_code = code;
  });

  const dialCode = computed(() => getCountryProfile(form.value.country_code).dialCode);

  const rules = {
    form: {
      name: {
        required: helpers.withMessage('Your name is required.', required),
        minLength: helpers.withMessage('Enter your full name.', minLength(2)),
        maxLength: maxLength(120),
      },
      email: {
        required: helpers.withMessage('Your work email is required.', required),
        email: helpers.withMessage('Enter a valid email address.', email),
      },
      phone: {
        pattern: helpers.withMessage('Enter a valid phone number.', (value: string) => !value || PHONE_PATTERN.test(value)),
      },
      institution_name: {
        required: helpers.withMessage('Your institution name is required.', required),
        minLength: helpers.withMessage('Enter your institution name.', minLength(2)),
        maxLength: maxLength(200),
      },
      message: { maxLength: helpers.withMessage('Keep the message under 2,000 characters.', maxLength(2000)) },
    },
  };

  const v$ = useVuelidate(rules, { form });

  type FieldKey = keyof DemoRequestForm;

  /** Client-side message first, then the server's message for that field. */
  function fieldError(field: FieldKey): string {
    const ruleSet = v$.value.form as unknown as Record<string, { $errors?: { $message: unknown }[] } | undefined>;
    const clientError = ruleSet[field]?.$errors?.[0]?.$message;
    if (clientError) return String(clientError);
    return getErrorMessage(field);
  }

  function selectCountry(code: string | null) {
    countryTouched.value = true;
    form.value.country_code = code;
  }

  function reset() {
    form.value = emptyDemoRequestForm(countryCode.value);
    countryTouched.value = false;
    submitted.value = false;
    successMessage.value = '';
    clearErrors();
    v$.value.$reset();
  }

  async function submit(): Promise<boolean> {
    clearErrors();
    if (!(await v$.value.$validate())) return false;

    const source = countryTouched.value ? DemoCountrySource.Manual : countrySource.value;
    try {
      const response = await requestDemo(buildDemoRequestInput(form.value, demoKind.value, dialCode.value, source));
      submitted.value = true;
      successMessage.value = response?.message ?? 'Thanks — we\'ll be in touch shortly.';
      return true;
    } catch (error) {
      handleServerErrors(error);
      if (Object.keys(serverErrors.value).length === 0) {
        toast.add({
          severity: 'error',
          summary: 'Request not sent',
          detail: toUserMessage(error, 'We could not send your request. Please try again or email support@gradeverse.com.'),
          life: 8000,
        });
      }
      return false;
    }
  }

  return {
    form,
    v$,
    demoKind,
    dialCode,
    isSubmitting,
    submitted,
    successMessage,
    fieldError,
    selectCountry,
    submit,
    reset,
  };
}
